import { Layout } from 'components/Layout/Layout';
import { Loader } from 'components/Loader/Loader';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { UserContext } from 'context/userContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createDepositRequest,
  getOpenDepositRequests,
  OpenDepositRequestRow,
} from 'services/api/depositService';
import styles from '../styles/pages/Deposit.module.css';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export const Deposit = () => {
  const { loggedUser, webSocket } = useContext(UserContext);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [amountInput, setAmountInput] = useState('');
  const [rows, setRows] = useState<OpenDepositRequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const amountValue = useMemo(() => Number(amountInput), [amountInput]);
  const canSubmit =
    Number.isFinite(amountValue) &&
    amountValue > 0 &&
    !submitting &&
    !!loggedUser;

  useEffect(() => {
    if (!loggedUser) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const data = await getOpenDepositRequests(loggedUser);
      if (!cancelled) {
        setRows(data);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [loggedUser]);

  useEffect(() => {
    if (!webSocket) {
      return;
    }
    const onMessage = (e: MessageEvent) => {
      let data: { type?: string; rows?: OpenDepositRequestRow[] };
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      if (data.type === 'open_deposit_requests' && Array.isArray(data.rows)) {
        setRows(data.rows);
      }
    };
    webSocket.addEventListener('message', onMessage);
    return () => webSocket.removeEventListener('message', onMessage);
  }, [webSocket]);

  if (!loggedUser) {
    navigate('/login');
    return null;
  }

  const copyDepositCode = async (code: string) => {
    const ok = await copyTextToClipboard(code);
    if (ok) {
      toast.success(t('deposit_code_copied'));
    } else {
      toast.error(t('deposit_code_copy_failed'));
    }
  };

  const onSubmit = async () => {
    if (!canSubmit || !loggedUser) return;
    setSubmitting(true);
    setError('');
    const created = await createDepositRequest(loggedUser, amountValue);
    if (!created) {
      setError('Unable to create deposit request. Please try again.');
      setSubmitting(false);
      return;
    }
    setAmountInput('');
    const data = await getOpenDepositRequests(loggedUser);
    setRows(data);
    setSubmitting(false);
  };

  const codeCopyTitle = isMobile
    ? t('deposit_code_tap_hint')
    : t('deposit_code_copy_hint');

  const content = (
    <div
      className={classNames(styles.wrapper, isMobile && styles.wrapperMobile)}
    >
      <h2 className={styles.title}>{t('deposit_label').toUpperCase()}</h2>

      <div className={styles.formCard}>
        <label className={styles.field}>
          <span>{t('deposit_column_amount')}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            autoComplete="transaction-amount"
          />
        </label>
        <button type="button" onClick={onSubmit} disabled={!canSubmit}>
          {submitting ? t('loading_label') : t('send_label')}
        </button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <h3 className={styles.sectionTitle}>
        {t('deposit_open_deposits_title')}
      </h3>

      {isMobile ? (
        <div className={styles.cardsWrap}>
          {loading ? (
            <div className={styles.cardsLoader}>
              <Loader inline />
            </div>
          ) : null}
          {!loading &&
            rows.map((row) => (
              <section
                key={row.id}
                className={styles.depositCard}
                aria-label={`${t('deposit_open_deposits_title')} #${row.id}`}
              >
                <div className={styles.cardLine}>
                  <span className={styles.cardLabel}>
                    {t('deposit_column_id')}
                  </span>
                  <span className={styles.cardValue}>{row.id}</span>
                </div>
                <div className={styles.cardLine}>
                  <span className={styles.cardLabel}>
                    {t('deposit_column_code')}
                  </span>
                  <span className={styles.cardCodeCell}>
                    <button
                      type="button"
                      className={styles.codeCopyTouch}
                      title={codeCopyTitle}
                      aria-label={codeCopyTitle}
                      onClick={() => copyDepositCode(row.code)}
                    >
                      {row.code}
                    </button>
                  </span>
                </div>
                <div className={styles.cardLine}>
                  <span className={styles.cardLabel}>
                    {t('deposit_column_date')}
                  </span>
                  <span
                    className={classNames(styles.cardValue, styles.cardDate)}
                  >
                    {row.created_at}
                  </span>
                </div>
                <div className={styles.cardLine}>
                  <span className={styles.cardLabel}>
                    {t('deposit_column_amount')}
                  </span>
                  <span className={styles.cardValue}>
                    {row.amount.toFixed(2)}
                  </span>
                </div>
                <div className={styles.cardLine}>
                  <span className={styles.cardLabel}>
                    {t('deposit_column_status')}
                  </span>
                  <span
                    className={classNames(styles.cardValue, styles.cardStatus)}
                  >
                    {row.status}
                  </span>
                </div>
              </section>
            ))}
          {!loading && rows.length === 0 ? (
            <div className={styles.empty}>{t('deposit_no_open_requests')}</div>
          ) : null}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <div className={styles.headerRow}>
            <div>{t('deposit_column_id')}</div>
            <div>{t('deposit_column_code')}</div>
            <div>{t('deposit_column_date')}</div>
            <div>{t('deposit_column_amount')}</div>
            <div>{t('deposit_column_status')}</div>
          </div>
          {loading && <Loader />}
          {!loading &&
            rows.map((row) => (
              <div key={row.id} className={styles.row}>
                <div>{row.id}</div>
                <button
                  type="button"
                  className={styles.codeCopy}
                  title={codeCopyTitle}
                  onClick={() => copyDepositCode(row.code)}
                >
                  {row.code}
                </button>
                <div>{row.created_at}</div>
                <div>{row.amount.toFixed(2)}</div>
                <div>{row.status}</div>
              </div>
            ))}
          {!loading && rows.length === 0 ? (
            <div className={styles.empty}>{t('deposit_no_open_requests')}</div>
          ) : null}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return <MobileLayout pageTitle="deposit_label">{content}</MobileLayout>;
  }
  return <Layout whiteBg>{content}</Layout>;
};
